import { z } from 'zod';

/**
 * Payroll form rules.
 *
 * Mirrors `backend/src/modules/institute/payroll/payroll.validation.ts`. These
 * are for fast feedback only — the server checks every one again, and works out
 * every total itself (CLAUDE.md §6).
 *
 * The forms hold what the inputs hold — strings — and these schemas turn them
 * into the numbers the API takes. A blank optional amount leaves as `null`.
 */

/** Same guard as the server: an extra zero is the commonest salary typo. */
export const MAX_SALARY_AMOUNT = 10_000_000;

const amountText = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `Enter ${label}`)
    .regex(/^\d+(\.\d{1,2})?$/, 'Numbers only, up to two decimal places')
    .transform(Number)
    .pipe(
      z
        .number()
        .min(0, 'An amount cannot be negative')
        .max(MAX_SALARY_AMOUNT, 'That looks too high — check the amount'),
    );

const optionalAmountText = z
  .string()
  .trim()
  .transform((value) => value || null)
  .pipe(
    z
      .string()
      .regex(/^\d+(\.\d{1,2})?$/, 'Numbers only, up to two decimal places')
      .transform(Number)
      .pipe(z.number().max(MAX_SALARY_AMOUNT, 'That looks too high — check the amount'))
      .nullable(),
  );

// ---------------------------------------------------------------------------
// pay heads
// ---------------------------------------------------------------------------

export const payHeadSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Give the pay head a name')
      .max(60, 'That name is too long')
      .regex(/^[\p{L}\p{N} .,'()&/-]+$/u, "Letters, numbers, spaces and . , ' ( ) & / - only"),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .min(1, 'Give the pay head a short code')
      .max(12, 'Twelve characters at most')
      .regex(/^[A-Z0-9_]+$/, 'Letters, numbers and _ only — for example HRA'),
    kind: z.enum(['EARNING', 'DEDUCTION'], {
      errorMap: () => ({ message: 'Choose whether this is an earning or a deduction' }),
    }),
    calculation: z.enum(['FIXED', 'PERCENT_OF_BASIC'], {
      errorMap: () => ({ message: 'Choose how this head is worked out' }),
    }),
    defaultValue: optionalAmountText,
    isStatutory: z.boolean(),
    isActive: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.calculation === 'PERCENT_OF_BASIC' && values.defaultValue !== null && values.defaultValue > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['defaultValue'],
        message: 'A percentage cannot be more than 100',
      });
    }
  });

export type PayHeadFormValues = z.input<typeof payHeadSchema>;

export const EMPTY_PAY_HEAD: PayHeadFormValues = {
  name: '',
  code: '',
  kind: 'EARNING',
  calculation: 'FIXED',
  defaultValue: '',
  isStatutory: false,
  isActive: true,
};

// ---------------------------------------------------------------------------
// salaries
// ---------------------------------------------------------------------------

export const salaryFormSchema = z.object({
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the date it starts from'),
  revisionType: z.enum(['JOINING', 'INCREMENT', 'PROMOTION', 'CORRECTION'], {
    errorMap: () => ({ message: 'Choose why this salary is being set' }),
  }),
  basic: amountText('the basic pay').refine((value) => value > 0, 'Basic pay must be more than zero'),
  /** One per head on the salary, keyed by the head's uuid. Blank lines are left off. */
  lines: z.array(
    z.object({
      componentUuid: z.string(),
      calculation: z.enum(['FIXED', 'PERCENT_OF_BASIC']),
      value: optionalAmountText,
    }),
  ),
  note: z
    .string()
    .trim()
    .max(255, 'Keep the note under 255 characters')
    .transform((value) => value || undefined),
});

export type SalaryFormValues = z.input<typeof salaryFormSchema>;

// ---------------------------------------------------------------------------
// salary payments
// ---------------------------------------------------------------------------

/**
 * Recording a month as paid. Mirrors `salaryPaymentSchema` in the server's
 * `payroll.validation.ts`; the month is fixed by the row it was opened from.
 */
export const salaryPaymentFormSchema = z.object({
  amount: amountText('the amount paid').refine((value) => value > 0, 'Enter the amount paid'),
  paidOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the day it was paid'),
  mode: z.enum(['CASH', 'BANK_TRANSFER'], {
    errorMap: () => ({ message: 'Choose cash or bank transfer' }),
  }),
  reference: z
    .string()
    .trim()
    .max(60, 'Keep the reference under 60 characters')
    .transform((value) => value || undefined),
  note: z
    .string()
    .trim()
    .max(255, 'Keep the note under 255 characters')
    .transform((value) => value || undefined),
});

export type SalaryPaymentFormValues = z.input<typeof salaryPaymentFormSchema>;
